import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { bandOf } from '../../ui/score';
import { errorText, rpc, type AppError } from '../admin/api';
import { loc, type Loc } from '../../lib/loc';
import { StaffFrame } from '../r3/common';
import { useSchoolDates } from './useSchoolDates';
import { termLabel } from '../../lib/r3format';
import { HeadFrame, type HeadHomeData } from './HeadPages';
import { EventList, type CalEvent } from './R3Panels';

// Release 3 (0019) screens for the head of school: What works (J3), Staff
// and students (J8), the school calendar (J9), plus the early-signal and
// THL panels shown on School results.

// ── J3 What works ──────────────────────────────────────────────────────────

export type WwRow = {
  scope: string; key: string; topic: string | null; schools_band: string; actions: number;
  before_mean: number; after_mean: number; mood_change: number;
  topic_before: number | null; topic_after: number | null; topic_change: number | null;
  baseline_mood_change: number | null; baseline_topic_change: number | null; title: Loc | null;
};
export type WhatWorks = {
  term: string | null; rows: WwRow[]; min_actions: number; min_responses: number;
  min_schools_country: number; min_schools_municipality: number; municipality_code: string | null;
  school_takes_part: boolean | null; computed_terms: string[];
};

export function WhatWorksPage() {
  const { t } = useI18n();
  return <HeadFrame tab="whatworks" title={t['r3.ww.title']}>{() => <WhatWorksBody />}</HeadFrame>;
}

function WhatWorksBody() {
  const { t } = useI18n();
  const [d, setD] = useState<WhatWorks | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<WhatWorks>('what_works').then((r) => (r.error ? setErr(r.error) : setD(r.data)));
  }, []);
  if (err) return <p className="notice notice-block" role="alert">{err.code === 'premium_required' ? t['r3.premiumWw'] : errorText(t, err)}</p>;
  if (!d) return <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">
        {d.school_takes_part ? t['r3.ww.takesPart'] : t['r3.ww.notTakingPart']}
      </p>
      <WhatWorksTable data={d} />
    </>
  );
}

const signedNum = (n: number | null, f: (x: number) => string) => (n === null ? '–' : n > 0 ? `+${f(n)}` : n < 0 ? `−${f(-n)}` : f(0));
const signedPts = (n: number | null) => (n === null ? '–' : n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0');

/** The What works table (heads, coordinators, platform admins). */
export function WhatWorksTable({ data }: { data: WhatWorks }) {
  const { t, lang, num } = useI18n();
  const id = useId();
  const scopes = Array.from(new Set(data.rows.map((r) => r.scope)));
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {data.term ? t['r3.ww.term'](termLabel(t, data.term)) : t['r3.ww.title']} <Pill kind="warn">{t['ts.draft']}</Pill>
      </h2>
      <p className="form-hint">{t['r3.ww.how'](data.min_schools_country, data.min_actions, data.min_responses)}</p>
      {!data.term || data.rows.length === 0 ? (
        <p className="text-muted">{data.term ? t['r3.ww.none'] : t['r3.ww.notYet']}</p>
      ) : (
        scopes.map((sc) => (
          <div key={sc} className="table-wrap" tabIndex={0} role="region" aria-label={sc === 'FI' ? t['r3.ww.country'] : t['r3.ww.municipality'](sc)}>
            <table className="table">
              <caption>{sc === 'FI' ? t['r3.ww.country'] : t['r3.ww.municipality'](sc)}</caption>
              <thead>
                <tr>
                  <th scope="col">{t['r3.ww.activity']}</th>
                  <th scope="col">{t['r3.ww.schools']}</th>
                  <th scope="col">{t['r3.ww.tried']}</th>
                  <th scope="col">{t['r3.ww.mood']}</th>
                  <th scope="col">{t['r3.ww.compare']}</th>
                  <th scope="col">{t['r3.ww.topic']}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.filter((r) => r.scope === sc).map((r) => (
                  <tr key={r.key}>
                    <th scope="row">
                      {loc(r.title ?? undefined, lang) || r.key}
                      <span className="text-muted"> · {r.topic === 'any' || !r.topic ? t['h2.pbAnyTopic'] : t[`topic.${r.topic}` as 'topic.exams']}</span>
                    </th>
                    <td>{r.schools_band}</td>
                    <td>{r.actions}</td>
                    <td>{num(r.before_mean)} → {num(r.after_mean)} ({signedNum(r.mood_change, num)})</td>
                    <td>{signedNum(r.baseline_mood_change, num)}</td>
                    <td>{r.topic_change === null ? '–' : t['r3.ww.pts'](signedPts(r.topic_change))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
      <p className="form-hint">{t['r3.notCausal']} {t['r3.ww.compareHint']}</p>
    </section>
  );
}

// ── J8 Staff and students ──────────────────────────────────────────────────

type Dim = 'workload' | 'energy' | 'sleep' | 'stress';
type StaffDim = { status: 'ok' | 'insufficient' | 'suppressed_near_unanimous'; total?: number; mean?: number; counts?: Record<string, number> };
type StaffMonth = { month: string; students: { status: string; total?: number; mean?: number }; staff: Record<Dim, StaffDim> };

export function StaffViewPage() {
  const { t } = useI18n();
  return <HeadFrame tab="staff" title={t['r3.sv.title']}>{(_c, home) => <StaffViewBody home={home} />}</HeadFrame>;
}

function StaffViewBody({ home }: { home: HeadHomeData }) {
  const { t, locale, num } = useI18n();
  const [d, setD] = useState<{ months: StaffMonth[]; floor: number } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const dates = useSchoolDates();
  const scoped = home.me.scope_grades !== null;
  useEffect(() => {
    if (!scoped) rpc<{ months: StaffMonth[]; floor: number }>('head_staff_view', { p_months: 6 }).then((r) => (r.error ? setErr(r.error) : setD(r.data)));
  }, [scoped]);
  if (scoped) return <p className="notice notice-block">{t['r3.sv.wholeOnly']}</p>;
  if (err) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!d) return <p role="status">{t.loading}</p>;
  const mfmt = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  // B14: no months from before the school existed.
  const created = dates?.created_at ?? null;
  const cell = (s: StaffDim) =>
    s.status === 'ok' && s.mean != null ? (
      <span>{num(s.mean)}/5 <span className="text-muted">· {t['h2.n'](s.total ?? 0)}</span></span>
    ) : (
      <span className="text-muted">{s.status === 'suppressed_near_unanimous' ? t['h2.st.suppressed_near_unanimous'] : t['h2.st.insufficient']}</span>
    );
  return (
    <>
      <p className="notice notice-block">{t['r3.sv.intro'](d.floor)} <Pill kind="warn">{t['ts.draft']}</Pill></p>
      <section className="panel p5" aria-labelledby="sv-t">
        <h2 className="panel-title" id="sv-t">{t['r3.sv.tableTitle']}</h2>
        <div className="table-wrap" tabIndex={0} role="region" aria-labelledby="sv-t">
          <table className="table">
            <caption className="visually-hidden">{t['r3.sv.tableTitle']}</caption>
            <thead>
              <tr>
                <th scope="col">{t['r3.sv.month']}</th>
                <th scope="col">{t['r3.sv.students']}</th>
                {(['workload', 'energy', 'sleep', 'stress'] as Dim[]).map((x) => <th key={x} scope="col">{t[`r3.dim.${x}` as 'r3.dim.workload']}</th>)}
              </tr>
            </thead>
            <tbody>
              {d.months.filter((m) => !created || m.month.slice(0, 7) >= created.slice(0, 7)).map((m) => (
                <tr key={m.month}>
                  <th scope="row">{mfmt.format(new Date(m.month))}</th>
                  <td>
                    {m.students.status === 'ok' && m.students.mean != null ? (
                      <span>{num(m.students.mean)}/5 <Pill kind={bandOf(m.students.mean).key}>{t[`band.${bandOf(m.students.mean).key}`]}</Pill></span>
                    ) : (
                      <span className="text-muted">{t['h2.st.insufficient']}</span>
                    )}
                  </td>
                  {(['workload', 'energy', 'sleep', 'stress'] as Dim[]).map((x) => <td key={x}>{cell(m.staff[x])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="form-hint">{t['r3.sv.scale']}</p>
        <p className="form-hint">{t['r3.sv.never']}</p>
      </section>
    </>
  );
}

// ── J9 Calendar ────────────────────────────────────────────────────────────


export function CalendarPage() {
  const { t } = useI18n();
  return <HeadFrame tab="calendar" title={t['r3.cal.title']}>{(_c, home) => <CalendarEditor grades={home.grades ?? []} scope={home.me.scope_grades} />}</HeadFrame>;
}

/** School admins reach the calendar from their home. */
export function SchoolAdminCalendarPage() {
  const { t } = useI18n();
  return (
    <StaffFrame roles={['school_admin']} title={t['r3.cal.title']} extraNav={[{ icon: '📅', label: t['r3.nav.calendar'], href: '/calendar', active: true }]}>
      {() => <CalendarEditor grades={null} scope={null} />}
    </StaffFrame>
  );
}

export function CalendarEditor({ grades, scope }: { grades: { id: string; label: string }[] | null; scope: string[] | null }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [ev, setEv] = useState<{ events: CalEvent[]; can_edit: boolean } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [form, setForm] = useState<{ id: string | null; kind: CalEvent['kind']; title: string; starts: string; ends: string; grades: string[] }>({ id: null, kind: 'exam_week', title: '', starts: '', ends: '', grades: [] });
  const scopedGrades = scope === null ? grades : (grades ?? []).filter((g) => scope.includes(g.label));

  const load = useCallback(async () => {
    const r = await rpc<{ events: CalEvent[]; can_edit: boolean }>('school_calendar', {});
    if (r.error) setErr(r.error);
    else setEv(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc('school_save_calendar_event', { p_id: form.id, p_kind: form.kind, p_title: form.title, p_starts: form.starts || null, p_ends: form.ends || form.starts || null, p_grade_ids: form.grades });
    if (r.error) return setErr(r.error);
    announce(t['pa.saved']);
    setForm({ id: null, kind: 'exam_week', title: '', starts: '', ends: '', grades: [] });
    load();
  }
  async function remove(x: CalEvent) {
    const r = await rpc('school_delete_calendar_event', { p_id: x.id });
    if (r.error) return setErr(r.error);
    announce(t['r3.cal.deleted']);
    load();
  }

  if (!ev) return err ? <p className="notice notice-block" role="alert">{errorText(t, err)}</p> : <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{t['r3.cal.intro']}</p>
      {ev.can_edit && (
        <section className="panel p5" aria-labelledby={`${id}-f`}>
          <h2 className="panel-title" id={`${id}-f`}>{form.id ? t['r3.cal.edit'] : t['r3.cal.add']}</h2>
          <form className="form" onSubmit={save} noValidate>
            <div className="form-row">
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-k`}>{t['r3.cal.kind']}</label>
                <div className="select-wrap">
                  <select id={`${id}-k`} className="select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as CalEvent['kind'] })}>
                    {(['exam_week', 'holiday', 'event', 'other'] as const).map((k) => <option key={k} value={k}>{t[`r3.cal.k.${k}` as 'r3.cal.k.event']}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-ti`}>{t['r3.cal.what']}</label>
                <input id={`${id}-ti`} className="field" maxLength={80} value={form.title} aria-describedby={`${id}-tih`} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <p className="form-hint" id={`${id}-tih`}>{t['r3.cal.noNames']}</p>
              </div>
            </div>
            <div className="form-row">
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-s`}>{t['r3.cal.from']}</label>
                <input id={`${id}-s`} className="field" type="date" value={form.starts} onChange={(e) => setForm({ ...form, starts: e.target.value })} />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-e`}>{t['r3.cal.to']}</label>
                <input id={`${id}-e`} className="field" type="date" value={form.ends} onChange={(e) => setForm({ ...form, ends: e.target.value })} />
              </div>
            </div>
            {scopedGrades && scopedGrades.length > 0 && (
              <fieldset className="fieldset">
                <legend className="field-label">{scope === null ? t['r3.cal.gradesAll'] : t['r3.cal.gradesYours']}</legend>
                <div className="chip-row">
                  {scopedGrades.map((g) => {
                    const on = form.grades.includes(g.id);
                    return (
                      <label key={g.id} className={`chip-check${on ? ' on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => setForm({ ...form, grades: on ? form.grades.filter((x) => x !== g.id) : [...form.grades, g.id] })} />
                        {t['h2.grade'](g.label)}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}
            {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
            <div className="form-actions">
              {form.id && <button type="button" className="btn btn-ghost" onClick={() => setForm({ id: null, kind: 'exam_week', title: '', starts: '', ends: '', grades: [] })}>{t['form.cancel']}</button>}
              <button type="submit" className="btn btn-primary" disabled={form.title.trim().length < 2 || !form.starts}>{t['pa.save']}</button>
            </div>
          </form>
        </section>
      )}
      <section className="panel p5" aria-labelledby={`${id}-l`}>
        <h2 className="panel-title" id={`${id}-l`}>{t['r3.cal.coming']}</h2>
        <EventList events={ev.events} locale={locale} onEdit={ev.can_edit ? (x) => setForm({ id: x.id, kind: x.kind, title: x.title, starts: x.starts_on, ends: x.ends_on, grades: x.grade_ids ?? [] }) : undefined} onDelete={ev.can_edit ? remove : undefined} />
      </section>
    </>
  );
}
